import { adminNoCacheQueryOptions } from "@/routes/_app/admin/admin-query-options";
import { client, orpc } from "@/utils/orpc.client";
import { QUERY_REFETCH_INTERVALS, QUERY_STALE_TIMES } from "@/utils/query-policies";

export const ADMIN_OFFER_CLICK_RANGES = ["24h", "7d", "30d", "90d"] as const;
export type AdminOfferClickRange = (typeof ADMIN_OFFER_CLICK_RANGES)[number];
export type AdminOfferClickAnalytics = Awaited<
  ReturnType<typeof client.deals.adminGetOfferClickAnalytics>
>;

export function getAdminOfferClickAnalyticsQueryOptions(range: AdminOfferClickRange) {
  return {
    ...orpc.deals.adminGetOfferClickAnalytics.queryOptions({ input: { range } }),
    staleTime: QUERY_STALE_TIMES.adminAnalytics,
    refetchOnMount: true,
  };
}

export type AdminOfferClickStreamPage = Awaited<
  ReturnType<typeof client.deals.adminGetOfferClickStream>
>;
export type AdminOfferClickStreamItem = AdminOfferClickStreamPage["items"][number];

/** The Offer clicks tab's search params, which filter the click stream server-side. */
export type AdminOfferClickSearch = {
  channel?: string;
  q?: string;
  /** Provider key of the store to show. */
  store?: string;
};

type StreamCursor = string;

/** Click stream pages, newest first, refetched every minute while the tab is visible. */
export function getAdminOfferClickStreamQueryOptions(
  filters: AdminOfferClickSearch & { range: AdminOfferClickRange },
) {
  const input = {
    range: filters.range,
    providerKey: filters.store,
    source: filters.channel,
    // The API rejects a blank search, so whitespace-only input means no search.
    query: filters.q?.trim() || undefined,
  };
  return {
    ...adminNoCacheQueryOptions(
      orpc.deals.adminGetOfferClickStream.infiniteOptions({
        input: (cursor: StreamCursor | undefined) => ({ ...input, cursor }),
        initialPageParam: undefined as StreamCursor | undefined,
        getNextPageParam: (lastPage: { nextCursor: string | null }) =>
          lastPage.nextCursor ?? undefined,
      }),
    ),
    refetchInterval: QUERY_REFETCH_INTERVALS.adminOfferClickStream,
    refetchIntervalInBackground: false,
  };
}
